/**
 * `Changelog` — pagina che mostra le note di rilascio.
 *
 * Il contenuto è caricato a runtime dal file markdown statico
 * `public/CHANGELOG.md` (aggiornabile senza rebuild) ed è organizzato in una
 * fisarmonica (accordion) di sezioni, una per versione. Ogni versione è un
 * heading H1 (`# X.Y.Z`) seguito dal corpo (paragrafo introduttivo,
 * sottosezioni `###`, liste, ecc.). Le versioni sono in ordine dalla più
 * recente, così come appaiono nel file.
 *
 * Comportamento: si parte con l'ultima versione aperta e le altre chiuse; se se
 * ne seleziona una, le altre si chiudono (una sola aperta alla volta). Un click
 * sull'header già aperto lo richiude.
 *
 * Rotta pubblica `/changelog`, raggiunta dal link nel footer.
 */
import { useEffect, useState } from 'react';
import { CHANGELOG_MD_URL } from '../appInfo';
import { Markdown } from '../components/Markdown';
import { Alert, Loading, PageHeader } from '../components/ui';

/** Una sezione di versione estratta dal changelog. */
export interface ChangelogVersion {
  /** Testo dell'heading H1, es. "0.2.0". */
  title: string;
  /** Markdown del corpo della versione (senza la riga dell'H1). */
  body: string;
}

/**
 * Divide il markdown del changelog in sezioni di versione.
 *
 * La suddivisione avviene sugli heading di primo livello (righe che iniziano
 * con "# "). Ogni sezione raccoglie tutte le righe fino all'H1 successivo,
 * escludendo la riga dell'H1 stessa. L'ordine del file è preservato (più
 * recente per prima). Eventuale contenuto precedente al primo H1 è ignorato.
 *
 * Funzione pura, così è facilmente testabile.
 */
export function parseChangelogVersions(md: string): ChangelogVersion[] {
  const lines = md.replace(/\r\n/g, '\n').split('\n');
  const versions: ChangelogVersion[] = [];
  let current: { title: string; bodyLines: string[] } | null = null;

  for (const line of lines) {
    const heading = /^#\s+(.+)$/.exec(line);
    if (heading) {
      // Chiude la versione precedente prima di aprirne una nuova.
      if (current) {
        versions.push({ title: current.title, body: current.bodyLines.join('\n').trim() });
      }
      current = { title: heading[1].trim(), bodyLines: [] };
      continue;
    }
    // Ignora il contenuto prima del primo H1.
    if (current) {
      current.bodyLines.push(line);
    }
  }

  if (current) {
    versions.push({ title: current.title, body: current.bodyLines.join('\n').trim() });
  }

  return versions;
}

export default function Changelog() {
  const [versions, setVersions] = useState<ChangelogVersion[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Indice della versione aperta; -1 = tutte chiuse. Parte da 0 (più recente).
  const [openIndex, setOpenIndex] = useState<number>(0);

  useEffect(() => {
    let cancelled = false;
    fetch(CHANGELOG_MD_URL, { cache: 'no-store' })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Impossibile caricare il changelog (${res.status})`);
        }
        return res.text();
      })
      .then((text) => {
        if (!cancelled) setVersions(parseChangelogVersions(text));
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Errore imprevisto.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section className="changelog" aria-labelledby="changelog-title">
      <PageHeader
        title="Changelog"
        titleId="changelog-title"
        subtitle="Le novità e le modifiche del prodotto, dalla più recente."
      />
      {error && <Alert tone="error">{error}</Alert>}
      {!error && versions === null && <Loading />}
      {!error && versions !== null && (
        <div className="changelog__versions">
          {versions.map((version, index) => {
            const open = openIndex === index;
            const headerId = `changelog-version-header-${index}`;
            const panelId = `changelog-version-panel-${index}`;
            return (
              <div className="changelog__version" key={`${version.title}-${index}`}>
                <button
                  type="button"
                  id={headerId}
                  className="changelog__version-header"
                  aria-expanded={open}
                  aria-controls={panelId}
                  // Toggle: se già aperta la richiude, altrimenti apre solo questa.
                  onClick={() => setOpenIndex(open ? -1 : index)}
                >
                  <span className="changelog__version-title">{version.title}</span>
                  <span className="changelog__version-icon" aria-hidden="true" />
                </button>
                {open && (
                  <div
                    id={panelId}
                    role="region"
                    aria-labelledby={headerId}
                    className="changelog__version-panel"
                  >
                    <Markdown className="ui-prose" source={version.body} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
